import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Formik } from "formik";
import * as Yup from "yup";
import { useParams, useNavigate } from "react-router-dom";
import debounce from "lodash/debounce";
import NewCustomAsyncSelectDropdown from '../../common/NewCustomAsyncSelectDropdown.jsx';
import '../../Trainer/TrainerDashboard.css';
import '../../common/lms-pages.css';
import {
  createcourses,
  updatecourses,
  getcoursesById,
  getBillingCourses,
  getBillingBranches,
  getBillingDepartments,
} from '../../../../services/NewCourseModule.js';
import { useAuth } from '../../../../context/AuthContext';
import { toast } from '../../common/Toast/toast';

// Rendered on its own route, and inside the course list as a dialog.
// `recordId` stands in for the route param when embedded, and onDone /
// onCancel replace the navigation the page version does.
function CourseModule({ embedded = false, recordId, onDone, onCancel }) {
  const { id: routeId } = useParams();
  const id = recordId || routeId;
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  // Form values
  const [initialValues, setInitialValues] = useState({
    name: "",
    description: "",
    duration: "",
    code: "",
    branchOfCourse: "",
    departmentOfCourse: "",
    courseModuleIds: "",
  });

  // Selected objects for dropdowns
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  // Dropdown state for courses
  const [courseOptions, setCourseOptions] = useState([]);
  const [coursePage, setCoursePage] = useState(1);
  const [courseHasMore, setCourseHasMore] = useState(true);
  const [courseSearchTerm, setCourseSearchTerm] = useState("");
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [hasLoadedCourses, setHasLoadedCourses] = useState(false);
  const isFetchingCourses = useRef(false);

  // Dropdown state for branches
  const [branchOptions, setBranchOptions] = useState([]);
  const [branchPage, setBranchPage] = useState(1);
  const [branchHasMore, setBranchHasMore] = useState(true);
  const [branchSearchTerm, setBranchSearchTerm] = useState("");
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [hasLoadedBranches, setHasLoadedBranches] = useState(false);
  const isFetchingBranches = useRef(false);

  // Dropdown state for departments
  const [deptOptions, setDeptOptions] = useState([]);
  const [deptPage, setDeptPage] = useState(1);
  const [deptHasMore, setDeptHasMore] = useState(true);
  const [deptSearchTerm, setDeptSearchTerm] = useState("");
  const [loadingDepts, setLoadingDepts] = useState(false);
  const [hasLoadedDepts, setHasLoadedDepts] = useState(false);
  const isFetchingDepts = useRef(false);

  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required("Course name is required"),
    description: Yup.string().required("Description is required"),
    duration: Yup.string().required("Duration is required"),
    code: Yup.string().required("Course code is required"),
    branchOfCourse: Yup.string().required("Branch is required"),
    departmentOfCourse: Yup.string().required("Department is required"),
    courseModuleIds: Yup.string().required("Please select a course name"),
  });

  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();

  useEffect(() => {
    if (!hasPermission('list-course', 'add', 'lms')) {
      if (!hasShownToast.current) {
        toast.error('You do not have permission to add Courses.');
        navigate('/');
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  // Load dropdown options on mount (so they are available for edit mode)
  useEffect(() => {
    const loadInitialOptions = async () => {
      // Load first page of courses, branches, departments (empty search)
      await Promise.all([
        fetchCourses("", 1, true),
        fetchBranches("", 1, true),
        fetchDepartments("", 1, true),
      ]);
      setHasLoadedCourses(true);
      setHasLoadedBranches(true);
      setHasLoadedDepts(true);
    };
    loadInitialOptions();
  }, []);

  // Fetch course data for edit mode
  const fetchCourseData = async () => {
    try {
      setLoading(true);
      const response = await getcoursesById(id);
      const courseData = response.data;

      setInitialValues({
        name: courseData.name || "",
        description: courseData.description || "",
        duration: courseData.duration || "",
        code: courseData.code || "",
        branchOfCourse: courseData.branchOfCourse || "",
        departmentOfCourse: courseData.departmentOfCourse || "",
        courseModuleIds: courseData.courseModuleIds || "",
      });

      // Set selected objects for dropdowns
      // Course: create selected object and add to options if not present
      if (courseData.courseModuleIds) {
        const courseOption = {
          value: courseData.courseModuleIds,
          label: courseData.name,
        };
        setSelectedCourse(courseOption);
        // Add to options if not already there
        setCourseOptions(prev => {
          if (!prev.find(opt => opt.value === courseOption.value)) {
            return [courseOption, ...prev];
          }
          return prev;
        });
      }

      // Branch: create selected object using name (since API returns name as string)
      if (courseData.branchOfCourse) {
        // Try to find matching branch from loaded options
        const matchedBranch = branchOptions.find(opt => opt.label === courseData.branchOfCourse);
        if (matchedBranch) {
          setSelectedBranch(matchedBranch);
        } else {
          // Create placeholder
          const branchPlaceholder = {
            value: courseData.branchOfCourse,
            label: courseData.branchOfCourse,
          };
          setSelectedBranch(branchPlaceholder);
          // Add to options
          setBranchOptions(prev => {
            if (!prev.find(opt => opt.value === branchPlaceholder.value)) {
              return [branchPlaceholder, ...prev];
            }
            return prev;
          });
        }
      }

      // Department: similarly
      if (courseData.departmentOfCourse) {
        const matchedDept = deptOptions.find(opt => opt.label === courseData.departmentOfCourse);
        if (matchedDept) {
          setSelectedDepartment(matchedDept);
        } else {
          const deptPlaceholder = {
            value: courseData.departmentOfCourse,
            label: courseData.departmentOfCourse,
          };
          setSelectedDepartment(deptPlaceholder);
          setDeptOptions(prev => {
            if (!prev.find(opt => opt.value === deptPlaceholder.value)) {
              return [deptPlaceholder, ...prev];
            }
            return prev;
          });
        }
      }
    } catch (error) {
      toast.error("Failed to fetch course data");
      navigate('/list-course');
    } finally {
      setLoading(false);
    }
  };

  // Trigger fetchCourseData when entering edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchCourseData();
    }
  }, [id, isEditMode]);

  // ----- Course dropdown (billing-courses) -----
  const fetchCourses = useCallback(async (search = "", page = 1, reset = true) => {
    if (isFetchingCourses.current) return [];
    isFetchingCourses.current = true;
    setLoadingCourses(true);
    try {
      const res = await getBillingCourses(20, page, search);
      const items = res?.data || [];
      const pagination = res?.pagination || {};
      const hasMore = pagination.hasNext || items.length === 20;

      const newOptions = items.map(item => ({
        value: item.id,
        label: item.name || item.title || "Unnamed",
        original: item,
      }));

      if (reset) {
        setCourseOptions(newOptions);
        setCoursePage(1);
      } else {
        setCourseOptions(prev => {
          const combined = [...prev, ...newOptions];
          return combined.filter((v, i, a) => a.findIndex(t => t.value === v.value) === i);
        });
      }
      setCourseHasMore(hasMore);
      setCourseSearchTerm(search);
      return newOptions;
    } catch (err) {
      console.error("Error fetching courses:", err);
      toast.error("Failed to load courses");
      return [];
    } finally {
      setLoadingCourses(false);
      isFetchingCourses.current = false;
    }
  }, []);

  const loadCoursesOnOpen = useCallback(async () => {
    if (!hasLoadedCourses && courseOptions.length === 0 && !loadingCourses) {
      await fetchCourses("", 1, true);
      setHasLoadedCourses(true);
    }
  }, [hasLoadedCourses, courseOptions.length, loadingCourses, fetchCourses]);

  const handleCourseSearch = useMemo(
    () => debounce(async (inputValue) => {
      if (inputValue === courseSearchTerm) return;
      setCoursePage(1);
      await fetchCourses(inputValue, 1, true);
    }, 700),
    [fetchCourses, courseSearchTerm]
  );

  const handleCourseScrollToBottom = useCallback(async () => {
    if (!courseHasMore || loadingCourses || isFetchingCourses.current) return;
    const nextPage = coursePage + 1;
    setCoursePage(nextPage);
    await fetchCourses(courseSearchTerm, nextPage, false);
  }, [courseHasMore, loadingCourses, coursePage, courseSearchTerm, fetchCourses]);

  const refreshCourses = useCallback(async () => {
    setCourseSearchTerm("");
    setCoursePage(1);
    setHasLoadedCourses(false);
    setCourseOptions([]);
    await fetchCourses("", 1, true);
    setHasLoadedCourses(true);
  }, [fetchCourses]);

  // ----- Branch dropdown (billing-branches) -----
  const fetchBranches = useCallback(async (search = "", page = 1, reset = true) => {
    if (isFetchingBranches.current) return [];
    isFetchingBranches.current = true;
    setLoadingBranches(true);
    try {
      const res = await getBillingBranches(20, page, search);
      const items = res?.data || [];
      const pagination = res?.pagination || {};
      const hasMore = pagination.hasNext || items.length === 20;

      const newOptions = items.map(item => ({
        value: item.id,
        label: item.name || item.title || "Unnamed",
        original: item,
      }));

      if (reset) {
        setBranchOptions(newOptions);
        setBranchPage(1);
      } else {
        setBranchOptions(prev => {
          const combined = [...prev, ...newOptions];
          return combined.filter((v, i, a) => a.findIndex(t => t.value === v.value) === i);
        });
      }
      setBranchHasMore(hasMore);
      setBranchSearchTerm(search);
      return newOptions;
    } catch (err) {
      console.error("Error fetching branches:", err);
      toast.error("Failed to load branches");
      return [];
    } finally {
      setLoadingBranches(false);
      isFetchingBranches.current = false;
    }
  }, []);

  const loadBranchesOnOpen = useCallback(async () => {
    if (!hasLoadedBranches && branchOptions.length === 0 && !loadingBranches) {
      await fetchBranches("", 1, true);
      setHasLoadedBranches(true);
    }
  }, [hasLoadedBranches, branchOptions.length, loadingBranches, fetchBranches]);

  const handleBranchSearch = useMemo(
    () => debounce(async (inputValue) => {
      if (inputValue === branchSearchTerm) return;
      setBranchPage(1);
      await fetchBranches(inputValue, 1, true);
    }, 700),
    [fetchBranches, branchSearchTerm]
  );

  const handleBranchScrollToBottom = useCallback(async () => {
    if (!branchHasMore || loadingBranches || isFetchingBranches.current) return;
    const nextPage = branchPage + 1;
    setBranchPage(nextPage);
    await fetchBranches(branchSearchTerm, nextPage, false);
  }, [branchHasMore, loadingBranches, branchPage, branchSearchTerm, fetchBranches]);

  const refreshBranches = useCallback(async () => {
    setBranchSearchTerm("");
    setBranchPage(1);
    setHasLoadedBranches(false);
    setBranchOptions([]);
    await fetchBranches("", 1, true);
    setHasLoadedBranches(true);
  }, [fetchBranches]);

  // ----- Department dropdown (billing-departments) -----
  const fetchDepartments = useCallback(async (search = "", page = 1, reset = true) => {
    if (isFetchingDepts.current) return [];
    isFetchingDepts.current = true;
    setLoadingDepts(true);
    try {
      const res = await getBillingDepartments(20, page, search);
      const items = res?.data || [];
      const pagination = res?.pagination || {};
      const hasMore = pagination.hasNext || items.length === 20;

      const newOptions = items.map(item => ({
        value: item.id,
        label: item.name || item.title || "Unnamed",
        original: item,
      }));

      if (reset) {
        setDeptOptions(newOptions);
        setDeptPage(1);
      } else {
        setDeptOptions(prev => {
          const combined = [...prev, ...newOptions];
          return combined.filter((v, i, a) => a.findIndex(t => t.value === v.value) === i);
        });
      }
      setDeptHasMore(hasMore);
      setDeptSearchTerm(search);
      return newOptions;
    } catch (err) {
      console.error("Error fetching departments:", err);
      toast.error("Failed to load departments");
      return [];
    } finally {
      setLoadingDepts(false);
      isFetchingDepts.current = false;
    }
  }, []);

  const loadDeptsOnOpen = useCallback(async () => {
    if (!hasLoadedDepts && deptOptions.length === 0 && !loadingDepts) {
      await fetchDepartments("", 1, true);
      setHasLoadedDepts(true);
    }
  }, [hasLoadedDepts, deptOptions.length, loadingDepts, fetchDepartments]);

  const handleDeptSearch = useMemo(
    () => debounce(async (inputValue) => {
      if (inputValue === deptSearchTerm) return;
      setDeptPage(1);
      await fetchDepartments(inputValue, 1, true);
    }, 700),
    [fetchDepartments, deptSearchTerm]
  );

  const handleDeptScrollToBottom = useCallback(async () => {
    if (!deptHasMore || loadingDepts || isFetchingDepts.current) return;
    const nextPage = deptPage + 1;
    setDeptPage(nextPage);
    await fetchDepartments(deptSearchTerm, nextPage, false);
  }, [deptHasMore, loadingDepts, deptPage, deptSearchTerm, fetchDepartments]);

  const refreshDepts = useCallback(async () => {
    setDeptSearchTerm("");
    setDeptPage(1);
    setHasLoadedDepts(false);
    setDeptOptions([]);
    await fetchDepartments("", 1, true);
    setHasLoadedDepts(true);
  }, [fetchDepartments]);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      if (isEditMode) {
        await updatecourses(id, values);
        toast.success("Course updated successfully");
      } else {
        await createcourses(values);
        toast.success("Course created successfully");
        resetForm();
        setSelectedCourse(null);
        setSelectedBranch(null);
        setSelectedDepartment(null);
      }
      if (embedded) {
        onDone?.();
        return;
      }
      navigate('/list-course');
    } catch (error) {
      toast.error(`Failed to ${isEditMode ? "update" : "create"} course`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="att-picklist-foot">
        <span className="pg-spinner att-spinner" aria-hidden="true" />
        <span>Loading course...</span>
      </div>
    );
  }

  const leave = () => {
    if (embedded) onCancel?.();
    else navigate('/list-course');
  };

  return (
    <div className={embedded ? 'att-embed' : 'pg'}>
      {!embedded && (
        <header className="pg-head">
          <div className="pg-head-main">
            <h4 className="pg-title">
              {isEditMode ? 'Edit course' : 'Add course'}
            </h4>
          </div>
          <div className="pg-head-actions">
            <button type="button" className="pg-btn" onClick={leave}>
              Back to courses
            </button>
          </div>
        </header>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ values, isSubmitting, setFieldValue, errors, touched, handleSubmit: submit }) => {
          const err = (f) => (touched[f] ? errors[f] : '');
          const ready =
            String(values.name || '').trim() &&
            String(values.code || '').trim() &&
            !errors.name &&
            !errors.code &&
            !errors.courseModuleIds;

          return (
            <form className="att-form" noValidate onSubmit={submit}>
              <div className="att-stage">
                <div className="att-stage-inner">
                  {/* One step: six answers, asked once. Splitting them
                      across tabs would put two fields behind a Continue
                      button for no gain. */}
                  <section className="att-step">
                    <div className="mcq-options">
                      <div className="pg-field">
                        <label className="pg-label">Course name</label>
                        <NewCustomAsyncSelectDropdown
                          isMulti={false}
                          options={courseOptions}
                          value={selectedCourse ? [selectedCourse] : []}
                          onChange={(opts) => {
                            const opt =
                              Array.isArray(opts) && opts.length
                                ? opts[opts.length - 1]
                                : null;
                            setSelectedCourse(opt);
                            setFieldValue('name', opt ? opt.label : '');
                            setFieldValue('courseModuleIds', opt ? opt.value : '');
                          }}
                          placeholder="Select a course"
                          isSearchable={true}
                          searchInControl={true}
                          isLoading={loadingCourses}
                          onMenuOpen={loadCoursesOnOpen}
                          onInputChange={handleCourseSearch}
                          onMenuScrollToBottom={handleCourseScrollToBottom}
                          onRefresh={refreshCourses}
                          loadingMessage="Loading courses..."
                          noOptionsMessage="No courses available"
                          noSearchResultsMessage="No courses found"
                        />
                        {err('courseModuleIds') && (
                          <p className="att-error">{err('courseModuleIds')}</p>
                        )}
                      </div>

                      <div className="pg-field">
                        <label className="pg-label" htmlFor="cm-code">
                          Course code
                        </label>
                        <input
                          id="cm-code"
                          type="text"
                          className="pg-input"
                          placeholder="e.g. JAVA-01"
                          value={values.code || ''}
                          onChange={(e) => setFieldValue('code', e.target.value)}
                        />
                        {err('code') && <p className="att-error">{err('code')}</p>}
                      </div>
                    </div>

                    <div className="mcq-options">
                      <div className="pg-field">
                        <label className="pg-label" htmlFor="cm-duration">
                          Duration
                        </label>
                        <input
                          id="cm-duration"
                          type="text"
                          className="pg-input"
                          placeholder="e.g. 3 months"
                          value={values.duration || ''}
                          onChange={(e) =>
                            setFieldValue('duration', e.target.value)
                          }
                        />
                        {err('duration') && (
                          <p className="att-error">{err('duration')}</p>
                        )}
                      </div>

                      <div className="pg-field">
                        <label className="pg-label">Branch</label>
                        <NewCustomAsyncSelectDropdown
                          isMulti={false}
                          options={branchOptions}
                          value={selectedBranch ? [selectedBranch] : []}
                          onChange={(opts) => {
                            const opt =
                              Array.isArray(opts) && opts.length
                                ? opts[opts.length - 1]
                                : null;
                            setSelectedBranch(opt);
                            setFieldValue('branchOfCourse', opt ? opt.label : '');
                          }}
                          placeholder="Select a branch"
                          isSearchable={true}
                          searchInControl={true}
                          isLoading={loadingBranches}
                          onMenuOpen={loadBranchesOnOpen}
                          onInputChange={handleBranchSearch}
                          onMenuScrollToBottom={handleBranchScrollToBottom}
                          onRefresh={refreshBranches}
                          loadingMessage="Loading branches..."
                          noOptionsMessage="No branches available"
                          noSearchResultsMessage="No branches found"
                        />
                        {err('branchOfCourse') && (
                          <p className="att-error">{err('branchOfCourse')}</p>
                        )}
                      </div>

                      <div className="pg-field">
                        <label className="pg-label">Department</label>
                        <NewCustomAsyncSelectDropdown
                          isMulti={false}
                          options={deptOptions}
                          value={selectedDepartment ? [selectedDepartment] : []}
                          onChange={(opts) => {
                            const opt =
                              Array.isArray(opts) && opts.length
                                ? opts[opts.length - 1]
                                : null;
                            setSelectedDepartment(opt);
                            setFieldValue(
                              'departmentOfCourse',
                              opt ? opt.label : '',
                            );
                          }}
                          placeholder="Select a department"
                          isSearchable={true}
                          searchInControl={true}
                          isLoading={loadingDepts}
                          onMenuOpen={loadDeptsOnOpen}
                          onInputChange={handleDeptSearch}
                          onMenuScrollToBottom={handleDeptScrollToBottom}
                          onRefresh={refreshDepts}
                          loadingMessage="Loading departments..."
                          noOptionsMessage="No departments available"
                          noSearchResultsMessage="No departments found"
                        />
                        {err('departmentOfCourse') && (
                          <p className="att-error">{err('departmentOfCourse')}</p>
                        )}
                      </div>
                    </div>

                    <div className="pg-field">
                      <label className="pg-label" htmlFor="cm-desc">
                        Description
                        <span className="att-card-count">optional</span>
                      </label>
                      <textarea
                        id="cm-desc"
                        rows={4}
                        className="pg-input pg-textarea"
                        placeholder="What the course covers"
                        value={values.description || ''}
                        onChange={(e) =>
                          setFieldValue('description', e.target.value)
                        }
                      />
                      {err('description') && (
                        <p className="att-error">{err('description')}</p>
                      )}
                    </div>
                  </section>
                </div>
              </div>

              <div className="att-actions">
                <button type="button" className="pg-btn" onClick={leave}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="pg-btn pg-btn--main"
                  disabled={isSubmitting || !ready}
                >
                  {isSubmitting ? (
                    <>
                      <span className="pg-spinner att-spinner" aria-hidden="true" />
                      {isEditMode ? 'Updating...' : 'Creating...'}
                    </>
                  ) : isEditMode ? (
                    'Update course'
                  ) : (
                    'Create course'
                  )}
                </button>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
}

export default CourseModule;
